import Vue, { CreateElement } from 'src/vue'
import { Component } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { TimePicker } from '@src/elements/input/time-picker'
import { Button } from '@src/elements/button/button'
import { weekDays } from '@src/util/constants'
import { Radio } from '@src/elements/input/radio'
import { ContentBox } from 'src/elements/content-box/content-box'

import Club from '@src/data/club'
import ApiClient from '@src/lib/api_client'
import { API } from '@src/lib/api_path'
import { snakecaseKeys, track } from '@src/util/functions'
import { ClubScheduleDTO } from '@src/dto/club-dto'

import './schedule.sass'

@Component({
  computed: {
    club () {
      return this.$store.state.club
    }
  }
})
export class Schedule extends Vue {

  private club: Club
  private startupDays: any[] = []
  private shutdownDays: any[] = []

  private apiClientClub: ApiClient

  constructor () {
    super()
    this.apiClientClub = new ApiClient()
    track('/club/schedule')
  }

  buildDay (day, time: string) {  // ex: day = 'Monday', time = 'ShutdownTime|BootTime' (time param will be used to get shutdown or boot time
    let scheduleKey = day + time
    return {
      name: day,
      // do not display starting 0
      hour: this.club[scheduleKey] ? Number(this.club[scheduleKey].split(':')[0]) : null,
      min: this.club[scheduleKey] ? Number(this.club[scheduleKey].split(':')[1]) : null
    }
  }

  getClubDaySchedule () {
    weekDays.forEach((day) => {
      let shutdownDay = this.buildDay(day, 'ShutdownTime') // ex: shutdownDay = { day: Monday, hour: '02', min: '05'
      let startupDay = this.buildDay(day, 'BootTime')      // ex: startupDay = { day: Monday, hour: '02', min: '05'
      this.shutdownDays.push(shutdownDay)
      this.startupDays.push(startupDay)
    })
  }

  save () {
    this.setClubDaySchedule()
    return this.apiClientClub.put(API.TRAINERS_API.CLUB, { club: snakecaseKeys(new ClubScheduleDTO(this.club)) }).then(() => {
      this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
    })
  }

  setClubDaySchedule () {
    for (let i = 0; i < 7; i++) {
      const shutdownTime = !this.shutdownDays[i].hour ? null : `${`0${this.shutdownDays[i].hour}`.slice(-2)}:${`00${this.shutdownDays[i].min}`.slice(-2)}`
      const startupTime = !this.startupDays[i].hour ? null : `${`0${this.startupDays[i].hour}`.slice(-2)}:${`00${this.startupDays[i].min}`.slice(-2)}`
      this.club[this.shutdownDays[i].name + 'ShutdownTime'] = shutdownTime
      this.club[this.startupDays[i].name + 'BootTime'] = startupTime
    }
  }

  resetDay (index: number, days: any) {
    if (this.club.enableShutdownTime) {
      days[index].hour = ''
      days[index].min = ''
    }
  }

  invertShutdownOption () {
    this.club.enableShutdownTime = !this.club.enableShutdownTime
    return this.apiClientClub.put(API.TRAINERS_API.CLUB, { club: snakecaseKeys(new ClubScheduleDTO(this.club)) }).then(() => {
      this.$store.dispatch('showToast', { message: this.$t('ACKNOWLEDGEMENT.SUCCESSFUL_CHANGES'), fadeOut: true })
    })
  }

  render (h: CreateElement) {
    const startupDays = this.renderDays(h, this.startupDays)
    const shutdownDays = this.renderDays(h, this.shutdownDays)

    return <div class='schedule'>
      <div class='text-headers'>
        <Subtitle1 class='subtitle-paragraph'>{this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.ENABLE_SCHEDULE')}</Subtitle1>
        <Subtitle1>{this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.ENABLE_SCHEDULE_NOTE')}</Subtitle1>
        <el-radio-group>
          <Radio value={'true'} text={this.$t('ENABLE')} bindedProp={`${this.club.enableShutdownTime}`}
                 change={this.invertShutdownOption} ref='enableAudioHint' />
          <Radio value={'false'} text={this.$t('DISABLE')} bindedProp={`${this.club.enableShutdownTime}`}
                 change={this.invertShutdownOption} ref='enableAudioHint' />
        </el-radio-group>
        <Subtitle1 class='subtitle-paragraph'>{this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.DAILY_SHUTDOWN_TITLE')}</Subtitle1>
        <Subtitle1>{this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.DAILY_SHUTDOWN_NOTE')}</Subtitle1>
      </div>
      <ContentBox overflow='scrollable'>
        <div class='startup-shutdown' slot='content'>
          <div class='startup'>
            <Subtitle1 class='subtitle-paragraph'>{this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.STARTUP')}</Subtitle1>
            {startupDays}
          </div>
          <div class='shutdown'>
            <Subtitle1 class='subtitle-paragraph'>{this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.SHUTDOWN')}</Subtitle1>
            {shutdownDays}
          </div>
        </div>
        <div slot='bottom'>
          <Button type='elevated' class='apply-btn' label={this.$t('APPLY')} click={this.save} />
        </div>
      </ContentBox>
    </div>
  }

  created () {
    this.getClubDaySchedule()
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('MY_CLUB')} - ${this.$root.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.TAB_NAME')}`
  }

  protected renderDays (h: CreateElement, days: any) {
    return days.map((day, index) => {
      return <div class='day'>
        <span class='name'><Subtitle1>{this.$t('WEEK_DAYS.' + day.name.toUpperCase())}</Subtitle1></span>
        <span class='time'><TimePicker description={this.$t('PAGE.CLUB.STARTUP_SHUTDOWN_SCHEDULE.SELECT_A_TIME')} valueHours={`${day.hour !== null ? day.hour : ''}`}
                                       minValueHours={0} maxValueHours={23} changeHours={(value) => days[index].hour = value} keyupHours={(value) => days[index].hour = value}
                                       valueMinutes={`${day.min !== null ? day.min : ''}`} minValueMinutes={0} maxValueMinutes={59}
                                       changeMinutes={(value) => days[index].min = value}
                                       keyupMinutes={(value) => days[index].min = value} disabled={!this.club.enableShutdownTime} /></span>
        {day.hour || day.min ?
          <span class='reset'><Button type='remove' disabled={!this.club.enableShutdownTime} click={() => this.resetDay(index, days)} icon={'util/reset-arrow'}
                                      label={this.$t('RESET')} /></span>
          : <span />}
      </div>
    })
  }
}
